import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import {
  CalendarCheck, Users, Syringe, AlertCircle, Clock, Truck, MapPin,
  CheckCircle, XCircle, TrendingUp, Thermometer, IndianRupee, Activity, Phone
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const statusConfig = {
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  assigned: { label: 'Assigned', color: 'bg-purple-100 text-purple-800' },
  on_the_way: { label: 'On the Way', color: 'bg-yellow-100 text-yellow-800' },
  arrived: { label: 'Arrived', color: 'bg-orange-100 text-orange-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
};

const PIE_COLORS = ['#3b82f6', '#673c96', '#f59e0b', '#f37721', '#22c55e', '#ef4444'];
const STATUS_LABEL_MAP = {
  confirmed: 'Confirmed', assigned: 'Assigned', on_the_way: 'On the Way',
  arrived: 'Arrived', completed: 'Completed', cancelled: 'Cancelled'
};

function ChartCard({ title, icon: Icon, children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl p-5 shadow-sm border border-[#673c96]/10 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-[#673c96]" />}
        <h3 className="text-sm font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function ComplianceGauge({ value }) {
  const radius = 70;
  const circumference = Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 90 ? '#22c55e' : value >= 70 ? '#f37721' : '#ef4444';

  return (
    <div className="flex flex-col items-center justify-center">
      <svg width="160" height="100" viewBox="0 0 160 100">
        <path d="M 10 90 A 70 70 0 0 1 150 90" fill="none" stroke="#f5f3f7" strokeWidth="12" strokeLinecap="round" />
        <path d="M 10 90 A 70 70 0 0 1 150 90" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
        <text x="80" y="70" textAnchor="middle" fill="#311b52"
          style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '22px' }}>
          {`${value}%`}
        </text>
        <text x="80" y="92" textAnchor="middle" fill="#673c96"
          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '9px' }}>
          {`Compliance Rate`}
        </text>
      </svg>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-[#673c96]/10 p-3 text-xs">
      <p className="font-semibold text-[#311b52] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-semibold">{typeof p.value === 'number' && p.value > 999 ? `₹${(p.value / 1000).toFixed(1)}K` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      const h = { headers: getAuthHeaders() };
      try {
        const [dashRes, analyticsRes] = await Promise.all([
          axios.get(`${config.API}/admin/dashboard`, h),
          axios.get(`${config.API}/admin/analytics`, h),
        ]);
        setData(dashRes.data);
        setAnalytics(analyticsRes.data);
      } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [getAuthHeaders]);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />
    </div>
  );

  const stats = [
    { label: "Today's Bookings", value: data?.today_bookings || 0, icon: CalendarCheck, color: '#f37721' },
    { label: 'Total Bookings', value: data?.total_bookings || 0, icon: Clock, color: '#673c96' },
    { label: 'Total Customers', value: data?.total_customers || 0, icon: Users, color: '#3e195e' },
    { label: 'Active Nurses', value: data?.total_nurses || 0, icon: Syringe, color: '#ee4036' },
    { label: 'Callbacks', value: data?.pending_callbacks || 0, icon: Phone, color: '#673c96' },
    { label: 'Total Revenue', value: `₹${((analytics?.revenue?.total || 0) / 1000).toFixed(0)}K`, icon: IndianRupee, color: '#22c55e' },
    { label: 'Compliance', value: `${analytics?.fulfillment_kpi?.observation_compliance_rate || 0}%`, icon: Activity, color: '#3b82f6' },
  ];

  const statusPieData = (analytics?.status_distribution || []).map(s => ({
    name: STATUS_LABEL_MAP[s.status] || s.status,
    value: s.count
  }));

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      {/* New bookings alert */}
      {(data?.new_bookings || 0) > 0 && (
        <div data-testid="new-bookings-alert" className="bg-[#f37721]/10 border border-[#f37721]/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-[#f37721]" />
          <span className="text-sm font-medium text-[#311b52]">{data.new_bookings} new booking(s) awaiting assignment</span>
        </div>
      )}

      {/* Pending callbacks alert */}
      {(data?.pending_callbacks || 0) > 0 && (
        <Link to="/admin/callbacks" data-testid="pending-callbacks-alert"
          className="block bg-[#673c96]/10 border border-[#673c96]/30 rounded-xl p-4 hover:bg-[#673c96]/15 transition-colors">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-[#673c96]" />
            <span className="text-sm font-medium text-[#311b52]">
              {data.pending_callbacks} callback request(s) pending response
            </span>
            <span className="ml-auto text-xs font-semibold text-[#673c96]">View All</span>
          </div>
        </Link>
      )}

      {/* KPI Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map((s, i) => (
          <div key={i} data-testid={`stat-card-${i}`} className="bg-white rounded-xl p-4 shadow-sm border border-[#673c96]/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-medium text-[#673c96] uppercase tracking-wide" style={{ fontFamily: 'Montserrat' }}>{s.label}</span>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}12` }}>
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
            </div>
            <div className="text-xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Status breakdown */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-[#673c96]/10">
        <h3 className="text-sm font-semibold text-[#311b52] mb-3" style={{ fontFamily: 'Poppins' }}>Today's Status Breakdown</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {Object.entries(statusConfig).map(([key, cfg]) => (
            <div key={key} data-testid={`status-${key}`} className="text-center p-2.5 rounded-xl bg-[#fdfcff] border border-[#673c96]/5">
              <div className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{data?.statuses?.[key] || 0}</div>
              <span className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.color}`}>{cfg.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row 1: Booking Trend + Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Booking Trend (30 Days)" icon={TrendingUp} className="lg:col-span-2">
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.booking_trend || []} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="bookingGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f37721" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f37721" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#673c96' }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#673c96' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="bookings" name="Bookings" stroke="#f37721" strokeWidth={2} fill="url(#bookingGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Status Distribution" icon={Activity}>
          <div className="h-[220px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value" nameKey="name">
                  {statusPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      {/* Charts Row 2: Fulfillment KPI + Cold-Chain + Revenue Split */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ChartCard title="Observation Compliance" icon={CheckCircle} data-testid="compliance-gauge">
          <ComplianceGauge value={analytics?.fulfillment_kpi?.observation_compliance_rate || 0} />
          <div className="text-center mt-2 space-y-1">
            <p className="text-xs text-[#673c96]" style={{ fontFamily: 'Montserrat' }}>
              <span className="font-semibold text-[#311b52]">{analytics?.fulfillment_kpi?.total_completed || 0}</span> of {analytics?.fulfillment_kpi?.total_bookings || 0} bookings fully compliant
            </p>
            <p className="text-[10px] text-[#673c96]/70">30-min post-vaccination observation timer</p>
          </div>
        </ChartCard>

        <ChartCard title="Cold-Chain Integrity (24h)" icon={Thermometer}>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics?.cold_chain || []} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" />
                <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#673c96' }} interval={3} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 9, fill: '#673c96' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="min_safe" name="Min Safe" stroke="#22c55e" strokeDasharray="4 4" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="max_safe" name="Max Safe" stroke="#ef4444" strokeDasharray="4 4" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="avg_temp" name="Avg Temp (°C)" stroke="#673c96" strokeWidth={2} dot={{ r: 2, fill: '#673c96' }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-center text-[#673c96]/70 mt-1">Safe range: 2°C – 8°C · live icebox packets</p>
        </ChartCard>

        <ChartCard title="Revenue Split" icon={IndianRupee}>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.revenue?.split || []} margin={{ top: 5, right: 10, left: -10, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" />
                <XAxis type="number" tick={{ fontSize: 9, fill: '#673c96' }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 10, fill: '#311b52' }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" name="Amount" radius={[0, 6, 6, 0]} barSize={28}>
                  <Cell fill="#673c96" />
                  <Cell fill="#f37721" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-center mt-1">
            <span className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>₹{((analytics?.revenue?.total || 0) / 1000).toFixed(1)}K</span>
            <span className="text-[10px] text-[#673c96] ml-1">total revenue</span>
          </p>
        </ChartCard>
      </div>

      {/* Charts Row 3: Revenue Trend + Vaccine Popularity + Geo Density */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Monthly Revenue Trend" icon={TrendingUp}>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.revenue?.monthly || []} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#673c96' }} />
                <YAxis tick={{ fontSize: 9, fill: '#673c96' }} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                <Bar dataKey="medicine" name="Medicine" fill="#673c96" radius={[4, 4, 0, 0]} barSize={14} />
                <Bar dataKey="service" name="Service Fee" fill="#f37721" radius={[4, 4, 0, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Vaccine Popularity" icon={Syringe}>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.vaccine_popularity || []} margin={{ top: 5, right: 10, left: -15, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" />
                <XAxis type="number" tick={{ fontSize: 9, fill: '#673c96' }} />
                <YAxis type="category" dataKey="vaccine" tick={{ fontSize: 9, fill: '#311b52' }} width={85} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Bookings" fill="#3e195e" radius={[0, 6, 6, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Geographical Density" icon={MapPin}>
          <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
            {(analytics?.geographical_density || []).map((g, i) => {
              const maxBookings = Math.max(...(analytics?.geographical_density || []).map(x => x.bookings), 1);
              const pct = (g.bookings / maxBookings) * 100;
              return (
                <div key={g.area || `geo-${i}`} data-testid={`geo-area-${i}`} className="group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[#311b52] truncate max-w-[160px]" title={g.area}>{g.area}</span>
                    <span className="text-xs font-bold text-[#f37721]">{g.bookings}</span>
                  </div>
                  <div className="w-full bg-[#f5f3f7] rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `linear-gradient(90deg, #673c96, #f37721)` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* Bookings by Time Slot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Bookings by Time Slot" icon={Clock}>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.slot_distribution || []} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" />
                <XAxis dataKey="slot" tick={{ fontSize: 8, fill: '#673c96' }} />
                <YAxis tick={{ fontSize: 9, fill: '#673c96' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Bookings" fill="#f37721" radius={[6, 6, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Recent Bookings mini-table */}
        <ChartCard title="Recent Bookings" icon={CalendarCheck}>
          <div className="overflow-x-auto max-h-[180px]">
            <table className="w-full text-xs" data-testid="recent-bookings-table">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#673c96]/10">
                  <th className="text-left py-1.5 px-2 font-semibold text-[#673c96]">Recipient</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-[#673c96]">Vaccine</th>
                  <th className="text-left py-1.5 px-2 font-semibold text-[#673c96]">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_bookings || []).slice(0, 8).map((b) => {
                  const sc = statusConfig[b.status] || { label: b.status, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={b.id} className="border-b border-[#673c96]/5">
                      <td className="py-1.5 px-2 font-medium text-[#311b52]">{b.recipient_name}</td>
                      <td className="py-1.5 px-2 text-[#3e195e]">{b.vaccine_name_snapshot}</td>
                      <td className="py-1.5 px-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${sc.color}`}>{sc.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
